/**
 * RectFields — the X / Y / W / H number boxes for whatever is selected.
 *
 * The numbers read the way the surface is SEEN: X and Y are the item's TOP-LEFT
 * corner measured from the surface's TOP-LEFT corner (X to the right, Y
 * downward), and changing W or H keeps that corner where it is. The model
 * stores rectangles from the bottom-left — and on a mirrored wall its +x runs
 * screen-left — so each field converts on the way in and out.
 *
 * Something that stands on the floor (a door) has no bottom edge to move: its
 * Y is still where its top is, but changing Y or H changes its height.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { usePhoneSized } from './chrome/usePhoneSized';
import { Surface } from '../../types/sections';
import { Rect } from '../../calculations/sections';
import { DimensionInput } from '../ui/DimensionInput';
import { DESIGNER_STRINGS as S } from './strings';
import './designer.css';

export type RectField = 'x' | 'y' | 'width' | 'height';
/** A change to a rectangle, in the model's own (bottom-left origin) coordinates. */
export type RectPatch = Partial<Record<RectField, number>>;

export interface RectFieldsProps {
  rect: Rect;
  surface: Surface;
  /** The surface is drawn mirrored (left / right walls), so its +x runs screen-left. */
  xFlipped: boolean;
  /** Smallest width / height allowed. */
  minSize: number;
  /** Distance to keep from the surface's sides and top (openings' safe margin). Default 0. */
  inset?: number;
  /** Distance to keep from the bottom. Default: the same as `inset`. */
  bottomInset?: number;
  /** The bottom edge stays on the floor (doors). */
  onFloor?: boolean;
  /** Turns a typed height into an allowed one (SlatWall's 6" rows). */
  snapHeight?: (value: number, current: number) => number;
  /** `field` is the number box that was edited (for folding repeated steps into one undo). */
  onRect: (patch: RectPatch, field: RectField) => void;
}

const HALF_INCH_FT = 1 / 24;

export const RectFields: React.FC<RectFieldsProps> = ({
  rect,
  surface,
  xFlipped,
  minSize,
  inset = 0,
  bottomInset = inset,
  onFloor = false,
  snapHeight,
  onRect,
}) => {
  const { x, y, width, height } = rect;
  // On a phone the four boxes would cover the surface: they fold behind one button.
  const phone = usePhoneSized();
  const [open, setOpen] = useState(false);
  const isCeiling = surface.kind === 'ceiling';
  const leftGap = xFlipped ? surface.width - (x + width) : x;   // screen-left edge of surface → item
  const topGap = surface.height - (y + height);                 // top edge of surface → item

  const setX = (v: number) => onRect({ x: xFlipped ? surface.width - width - v : v }, 'x');
  const setY = (v: number) => (onFloor
    ? onRect({ height: surface.height - v }, 'y')                // top moves, bottom stays on the floor
    : onRect({ y: surface.height - height - v }, 'y'));
  const setWidth = (v: number) => onRect(xFlipped ? { width: v, x: x + width - v } : { width: v }, 'width'); // screen-left edge stays
  const setHeight = (v: number) => {
    const h = snapHeight ? snapHeight(v, height) : v;
    onRect(onFloor ? { height: h } : { height: h, y: y + height - h }, 'height'); // top edge stays (or the floor does)
  };

  const fields: { key: RectField; label: string; title: string; value: number; min: number; max: number; set: (v: number) => void }[] = [
    { key: 'x', label: S.fieldX, title: S.fieldXTitle, value: leftGap, min: inset, max: surface.width - inset - width, set: setX },
    {
      key: 'y', label: S.fieldY, title: isCeiling ? S.fieldYTitleCeiling : S.fieldYTitle, value: topGap, min: inset,
      max: onFloor ? surface.height - minSize : surface.height - bottomInset - height, set: setY,
    },
    { key: 'width', label: S.fieldWidth, title: S.fieldWidthTitle, value: width, min: minSize, max: surface.width - inset - leftGap, set: setWidth },
    {
      key: 'height', label: S.fieldHeight, title: S.fieldHeightTitle, value: height, min: minSize,
      max: onFloor ? surface.height - inset : surface.height - bottomInset - topGap, set: setHeight,
    },
  ];

  if (phone && !open) {
    return (
      <div className="designer-toolbar" style={{ display: 'flex', justifyContent: 'center' }}>
        <button type="button" onClick={() => setOpen(true)} aria-expanded={false} style={foldButton}>
          {S.sizeFields} <ChevronDown size={14} />
        </button>
      </div>
    );
  }
  return (
    <div className="designer-toolbar" style={{ display: 'flex', alignItems: 'center', gap: phone ? 8 : 14, flexWrap: 'wrap', justifyContent: 'center' }}>
      {phone && (
        <button type="button" onClick={() => setOpen(false)} aria-expanded style={{ ...foldButton, flexBasis: '100%' }}>
          {S.sizeFields} <ChevronUp size={14} />
        </button>
      )}
      {fields.map((f) => (
        <label key={f.key} title={f.title} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, margin: 0 }}>
          {f.label}
          <span style={{ width: 176, display: 'inline-block' }}>
            <DimensionInput
              value={f.value}
              // The field shows whole inches and reports on every blur, so a
              // value that still reads the same is not a change — without
              // this, tabbing through would quietly round 5' 1" (5.0833)
              // to 5.08 and count as an edit.
              onChange={(v) => { if (Math.abs(v - f.value) >= HALF_INCH_FT) f.set(v); }}
              mode="ftin"
              min={f.min}
              max={Math.max(f.min, f.max)}
              ariaLabel={f.title}
            />
          </span>
        </label>
      ))}
    </div>
  );
};

const foldButton: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 28, padding: '0 10px', borderRadius: 7, cursor: 'pointer',
  fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap',
  background: 'rgba(255,255,255,0.06)', border: '2px solid rgba(255,255,255,0.25)',
};

export default RectFields;
